import React, { useCallback, useEffect, useState } from 'react';
import dayjs from 'dayjs';
import objectHash from 'object-hash';
import MainTitle from 'base/components/MainTitle';
import Header from 'base/components/Header';
import HeaderMessage from 'base/components/HeaderMessage';
import {
  FileUploadIcon,
  FileDownloadIcon,
  CreateNewFolderIcon,
  DeleteIcon,
  EmailIcon,
  EditIcon,
} from 'base/components/Icon/index';
import Button from 'base/components/Button/index';
import theme from 'theme/theme';
import { getActiveSpace, updateSpaceBody } from 'spaces/index';
import { openModal } from 'modal/store/actions';
import { getAuthUserId, getIsAdmin } from 'auth/store/auth/selectors';
import NavLink from 'base/components/NavLink';
import ButtonGroup from 'base/components/ButtonGroup';
import Editor from 'editor/Editor';
import { useMatch } from 'react-router';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import ClassicalFileExplorer from '../ClassicalFileExplorer';
import {
  setActiveFolderId,
  setEditingFolderId,
  fetchFolderDocumentCountBySpaceId,
  updateFolder,
} from '../../store/folders/actions';
import { getActiveFolder, getActiveFolderChildren } from '../../store/folders/selectors';
import { downloadMultipleDocumentsAndFolders, fetchDocument, setActiveDocumentId } from '../../store/documents/actions';
import { setSelectedData } from '../../store/fileexplorer/actions';
import { getActiveFolderDocuments } from '../../store/documents/selectors';
import api from '../../../api';

export default function FileExplorerPage() {
  const dispatch = useAppDispatch();
  const space = useAppSelector(getActiveSpace);
  const folder = useAppSelector(getActiveFolder);
  const folders = useAppSelector(getActiveFolderChildren);
  const documents = useAppSelector(getActiveFolderDocuments);
  const isAdmin = useAppSelector(getIsAdmin);
  const myUserId = useAppSelector(getAuthUserId);
  const title = folder ? folder.name : space && space.name;
  const spaceMatch = useMatch('/spaces/:spaceId/*');
  const spaceId = spaceMatch?.params?.spaceId;
  const documentViewMatch = useMatch('/spaces/:spaceId/documents/document/:documentId');
  const folderMatch = useMatch('/spaces/:spaceId/documents/folder/:folderId/*');
  const folderId = folderMatch?.params?.folderId;
  const folderDocumentViewMatch = useMatch('/spaces/:spaceId/documents/folder/:folderId/document/:documentId');
  const documentId = documentViewMatch?.params?.documentId || folderDocumentViewMatch?.params?.documentId;

  const [isEditingBody, setIsEditingBody] = useState(false);
  const [body, setBody] = useState(folder ? folder.body : space ? space.body : null); // eslint-disable-line
  // used to force unmount and mount Editor component when folder or space changes
  // so that the Editor state is updated correctly
  const [bodyKey, setBodyKey] = useState(`${space && space.id}-${folder && folder.id}`);

  const handleDocumentView = useCallback(() => {
    dispatch(setActiveDocumentId(documentId));
    dispatch(fetchDocument(documentId));
    dispatch(openModal('DOCUMENT_VIEW'));
  }, [dispatch, documentId]);

  // Force unmount and mount Editor component when folder or space changes
  // We also add a hash of the body as the document or space body may be updated with a slight delay
  const bodyHash = objectHash(folder?.body || space?.body);
  const resetEditor = useCallback(() => {
    setIsEditingBody(false);
    if (folder?.body) {
      setBody(folder?.body);
    } else if (space.body) {
      setBody(space.body);
    } else {
      setBody(null);
    }
    setBodyKey(`${spaceId}-${folderId}-${bodyHash}`);
  }, [bodyHash, spaceId, folderId]); // eslint-disable-line

  useEffect(() => {
    dispatch(setActiveFolderId(folderId));
    dispatch(fetchFolderDocumentCountBySpaceId(spaceId));

    if (documentId) {
      handleDocumentView();
    }
  }, [dispatch, documentId, folderId, handleDocumentView, spaceId]);

  // Space changed
  useEffect(() => {
    dispatch(fetchFolderDocumentCountBySpaceId(spaceId));
    resetEditor();
  }, [dispatch, resetEditor, spaceId]);

  // Folder changed
  useEffect(() => {
    dispatch(setActiveFolderId(folderId));
    resetEditor();
  }, [dispatch, folderId, resetEditor]);

  // Document changed
  useEffect(() => {
    if (documentId) {
      handleDocumentView();
    }
  }, [dispatch, documentId, handleDocumentView]);

  const hasPermission = permission =>
    (folder && folder.permissions[permission]) || (!folder && space && space.defaultFolderPermissions[permission]);

  const hasBodyPermission = permission =>
    (folder && folder.permissions[permission]) || (!folder && space && space.defaultFolderPermissions[permission]);

  const handleUpload = () => {
    dispatch(openModal('DOCUMENT_UPLOAD'));
  };

  const handleCreateFolder = () => {
    dispatch(openModal('FOLDER_CREATE'));
  };

  const handleDeleteFolder = () => {
    dispatch(setEditingFolderId(folderId));
    dispatch(openModal('FOLDER_DELETE'));
  };

  const handleDownloadFolder = () => {
    dispatch(downloadMultipleDocumentsAndFolders(documents, folders, title));
  };

  const handleDownloadSpaceIndex = () => {
    const token = localStorage.getItem('accessToken');
    const url = `${process.env.REACT_APP_API_URL}/api/rest/spaces/${space.id}/index?jwt=${token}`;
    window.open(url, '_blank').focus();
    // this.props.downloadSpaceIndex(this.props.space);
  };

  const handleEditBody = () => {
    setIsEditingBody(true);
  };

  const handleBodyChange = value => {
    setBody(value);
  };

  const handleSaveBody = () => {
    const json = body.toJSON();
    if (folder) {
      dispatch(updateFolder({ ...folder, body: json }, false));
    } else {
      dispatch(updateSpaceBody({ ...space, body: json }));
    }
    setIsEditingBody(false);
  };

  const handleBodyImageUpload = async file => {
    if (folder) {
      const response = await api.folders.uploadInlineContentImage(folder, file).toPromise();
      return response.body.id;
    }
    const response = await api.spaces.uploadInlineContentImage(space, file).toPromise();
    return response.body.id;
  };

  const handleShare = () => {
    if (!folder) {
      return;
    }
    dispatch(setSelectedData([folder]));
    dispatch(openModal('DOCUMENT_SHARE'));
  };

  const isSpaceOwner = space && space.owner && space.owner === myUserId;

  const auditLinks = space && (
    <ul>
      {space.needsUserAudit && (
        <li>
          <NavLink to={`/spaces/${space.id}/members`}>Space members</NavLink>
        </li>
      )}
      {space.needsRoleAudit && (
        <li>
          <NavLink to={`/spaces/${space.id}/permissions`}>Space permissions</NavLink>
        </li>
      )}
    </ul>
  );

  const actionButtons = (
    <ButtonGroup pullRight>
      {hasPermission('canCreateDocument') && (
        <Button onClick={handleUpload}>
          <FileUploadIcon color={theme.colors.medium} /> Upload
        </Button>
      )}

      <Button onClick={handleDownloadFolder}>
        <FileDownloadIcon color={theme.colors.medium} /> Download folder
      </Button>

      <Button onClick={handleDownloadSpaceIndex}>
        <FileDownloadIcon color={theme.colors.medium} /> Space index
      </Button>

      {hasPermission('canShare') && folder && (
        <Button onClick={handleShare}>
          <EmailIcon color={theme.colors.medium} /> Share
        </Button>
      )}

      {hasPermission('canCreate') && (
        <Button onClick={handleCreateFolder}>
          <CreateNewFolderIcon color={theme.colors.medium} /> New folder
        </Button>
      )}

      {hasBodyPermission('canWrite') && (
        <Button onClick={handleEditBody}>
          <EditIcon color={theme.colors.medium} /> Edit description
        </Button>
      )}

      {folder && hasPermission('canDelete') && (
        <Button onClick={handleDeleteFolder}>
          <DeleteIcon color={theme.colors.medium} /> Delete folder
        </Button>
      )}
    </ButtonGroup>
  );

  return (
    <>
      {space &&
        (space.needsUserAudit || space.needsRoleAudit) &&
        space.auditDeadline &&
        (isAdmin || isSpaceOwner) &&
        (new Date(space.auditDeadline) < new Date() ? (
          <HeaderMessage
            type="warning"
            title="Permission review deadline expired"
            message={
              <div>
                <strong>
                  All users/roles have been blocked from this space.
                  <br />
                </strong>
                Please review the users and roles on the following pages to allow them to access this space again.
                {auditLinks}
              </div>
            }
          />
        ) : (
          <HeaderMessage
            type="warning"
            title="Permission review required"
            message={
              <div>
                Please review all users and roles until&nbsp;
                {dayjs(space.auditDeadline).format('DD.MM.YYYY')}
                ,&nbsp; or they will be blocked from accessing this space.
                {auditLinks}
              </div>
            }
          />
        ))}

      <Header style={{ position: 'relative' }}>
        <MainTitle>{title}</MainTitle>

        {isEditingBody ? (
          <ButtonGroup pullRight>
            <Button variant="primary" onClick={handleSaveBody}>
              Save description
            </Button>
          </ButtonGroup>
        ) : (
          actionButtons
        )}
      </Header>

      <Editor
        isReadOnly={!isEditingBody}
        initialValue={body}
        onChange={handleBodyChange}
        key={bodyKey}
        handleImageUpload={handleBodyImageUpload}
      />

      <ClassicalFileExplorer />
    </>
  );
}
